import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { FormItem } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    getTicketByFeedbackToken,
    submitFeedback,
} from '@/modules/Ticket/services'
import { TicketForFeedback } from '@/modules/Ticket/types/response'
import { formatFromUTC } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface FeedbackFormValues {
    rating_support_quality: number
    rating_staff_attitude: number
    feedback_text: string
}

interface SubmissionInfo {
    rating_support_quality: number
    rating_staff_attitude: number
    submittedAt: string
    submissionCount: number
}

const SUBMISSION_STORAGE_KEY = 'feedback_submission'

// Navbar component for public feedback pages
const FeedbackNavbar: React.FC = () => {
    return (
        <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
            <div className="max-w-full mx-auto px-4 py-3">
                <img
                    src="/images/icon.png"
                    alt="VNETWORK"
                    className="h-10 w-auto"
                />
            </div>
        </nav>
    )
}

const PublicFeedback: React.FC = () => {
    const { token } = useParams<{ token: string }>()
    const { t } = useTranslation()
    const [hoveredRating, setHoveredRating] = useState(0)
    const [selectedRating, setSelectedRating] = useState(0)
    const [hoveredAttitudeRating, setHoveredAttitudeRating] = useState(0)
    const [selectedAttitudeRating, setSelectedAttitudeRating] = useState(0)
    const [submitted, setSubmitted] = useState(false)
    const [previousSubmission, setPreviousSubmission] =
        useState<SubmissionInfo | null>(null)

    // Check for previous submission in localStorage
    useEffect(() => {
        if (!token) return

        try {
            const storageKey = `${SUBMISSION_STORAGE_KEY}_${token}`
            const stored = localStorage.getItem(storageKey)

            if (stored) {
                const submissionInfo: SubmissionInfo = JSON.parse(stored)
                setPreviousSubmission(submissionInfo)
            }
        } catch {
            console.warn('localStorage not available')
        }
    }, [token])

    // Save submission to localStorage
    const saveSubmissionToStorage = (
        qualityRating: number,
        attitudeRating: number
    ) => {
        if (!token) return

        try {
            const storageKey = `${SUBMISSION_STORAGE_KEY}_${token}`
            const stored = localStorage.getItem(storageKey)
            const now = new Date().toISOString()

            let submissionInfo: SubmissionInfo
            if (stored) {
                const prev: SubmissionInfo = JSON.parse(stored)
                submissionInfo = {
                    rating_support_quality: qualityRating,
                    rating_staff_attitude: attitudeRating,
                    submittedAt: now,
                    submissionCount: prev.submissionCount + 1,
                }
            } else {
                submissionInfo = {
                    rating_support_quality: qualityRating,
                    rating_staff_attitude: attitudeRating,
                    submittedAt: now,
                    submissionCount: 1,
                }
            }

            localStorage.setItem(storageKey, JSON.stringify(submissionInfo))
            setPreviousSubmission(submissionInfo)
        } catch {
            console.warn('localStorage not available')
        }
    }

    const { control, handleSubmit, setValue } = useForm<FeedbackFormValues>({
        defaultValues: {
            rating_support_quality: 0,
            rating_staff_attitude: 0,
            feedback_text: '',
        },
    })

    // Fetch ticket info by token
    const { data, renderState, isError } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.TICKETS_PUBLIC,
            'public-feedback',
            token,
        ] as const,
        queryFn: () => getTicketByFeedbackToken(token || ''),
        enabled: !!token,
    })

    // Submit feedback mutation
    const { mutateAsync: submitFeedbackMutation, isPending: isSubmitting } =
        useMutationWithHandler(
            { mutationFn: submitFeedback },
            {
                successMessage: t('feedback-submitted-success'),
                reactQueryKeys: QUERY_KEYS.TICKETS_PUBLIC,
                onSuccess: () => {
                    saveSubmissionToStorage(
                        selectedRating,
                        selectedAttitudeRating
                    )
                    setSubmitted(true)
                },
            }
        )

    const ticketInfo: TicketForFeedback | null = data?.result || null

    const handleRatingClick = (rating: number) => {
        setSelectedRating(rating)
        setValue('rating_support_quality', rating)
    }

    const handleAttitudeRatingClick = (rating: number) => {
        setSelectedAttitudeRating(rating)
        setValue('rating_staff_attitude', rating)
    }

    const onSubmit = async (formData: FeedbackFormValues) => {
        const qualityRating = formData.rating_support_quality || selectedRating
        const attitudeRating =
            formData.rating_staff_attitude || selectedAttitudeRating
        if (!token || qualityRating === 0 || attitudeRating === 0) return

        await submitFeedbackMutation({
            token,
            rating_support_quality: qualityRating,
            rating_staff_attitude: attitudeRating,
            feedback_text: formData.feedback_text || undefined,
        })
    }

    const StarIcon = ({ filled }: { filled: boolean }) => (
        <svg viewBox="0 0 20 20" className="w-full h-full">
            <path
                d="M3.825 19.5L5.45 12.475L0 7.75L7.2 7.125L10 0.5L12.8 7.125L20 7.75L14.55 12.475L16.175 19.5L10 15.775L3.825 19.5Z"
                fill={filled ? '#facc15' : '#d1d5db'}
            />
        </svg>
    )

    const renderStars = (
        type: 'quality' | 'attitude' = 'quality',
        interactive = true,
        displayRating?: number
    ) => {
        const currentRating =
            type === 'quality' ? selectedRating : selectedAttitudeRating
        const currentHovered =
            type === 'quality' ? hoveredRating : hoveredAttitudeRating
        const rating = displayRating ?? currentRating

        return Array.from({ length: 5 }, (_, i) => {
            const starValue = i + 1
            const isFilled =
                starValue <= (interactive ? currentHovered || rating : rating)

            if (!interactive) {
                return (
                    <div key={i} className="w-8 h-8">
                        <StarIcon filled={isFilled} />
                    </div>
                )
            }

            return (
                <button
                    key={i}
                    type="button"
                    className="w-10 h-10 transition-all duration-200 hover:scale-110"
                    onMouseEnter={() =>
                        type === 'quality'
                            ? setHoveredRating(starValue)
                            : setHoveredAttitudeRating(starValue)
                    }
                    onMouseLeave={() =>
                        type === 'quality'
                            ? setHoveredRating(0)
                            : setHoveredAttitudeRating(0)
                    }
                    onClick={() =>
                        type === 'quality'
                            ? handleRatingClick(starValue)
                            : handleAttitudeRatingClick(starValue)
                    }
                >
                    <StarIcon filled={isFilled} />
                </button>
            )
        })
    }

    // Error states - token invalid/expired
    if (isError) {
        return (
            <div className="min-h-screen bg-[#FEF2F2]">
                <FeedbackNavbar />
                <div className="pt-20 pb-8 px-4 flex items-center justify-center min-h-screen">
                    <div className="bg-white rounded-[16px] shadow-lg p-8 max-w-md w-full text-center">
                        {/* Title */}
                        <h2 className="text-xl font-medium text-gray-800 mb-4">
                            {t('feedback-survey-ended-title')}
                        </h2>

                        {/* Description */}
                        <p className="text-gray-600 mb-8">
                            {t('feedback-survey-ended-description')}
                        </p>

                        {/* Contact Options */}
                        <div className="space-y-3">
                            {/* Hotline Button */}
                            <a
                                href="tel:19006469"
                                className="flex items-center justify-center gap-3 w-full bg-[#ee2424] text-white py-4 px-6 rounded-md hover:bg-[#cc1f1f] transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                                <span className="font-medium">
                                    {t('feedback-call-hotline')}
                                </span>
                            </a>

                            {/* Email Button */}
                            <a
                                href="mailto:support@vnetwork.vn"
                                className="flex items-center justify-center gap-3 w-full border-2 border-[#ee2424] text-[#ee2424] py-4 px-6 rounded-md hover:bg-[#FEF2F2] transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                                <div className="text-left">
                                    <span className="font-medium block">
                                        {t('feedback-send-email')}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        support@vnetwork.vn
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // Already submitted (from API) or just submitted
    if (ticketInfo?.already_submitted || submitted) {
        const showQualityRating = submitted
            ? selectedRating
            : previousSubmission?.rating_support_quality
        const showAttitudeRating = submitted
            ? selectedAttitudeRating
            : previousSubmission?.rating_staff_attitude
        const isResubmission =
            previousSubmission && previousSubmission.submissionCount > 1

        return (
            <div className="min-h-screen bg-[#FEF2F2]">
                <FeedbackNavbar />
                <div className="pt-20 pb-8 px-4 flex items-center justify-center min-h-screen">
                    <div className="bg-white rounded-md shadow-lg p-8 max-w-lg w-full text-center overflow-visible">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                                className="w-10 h-10 text-[#ee2424]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-[#ee2424] mb-3">
                            {t('feedback-thank-you')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {isResubmission
                                ? t('feedback-thank-you-resubmitted')
                                : t('feedback-thank-you-submitted')}
                        </p>

                        {/* Show submitted ratings */}
                        <div className="space-y-4 mb-4">
                            {showQualityRating && showQualityRating > 0 && (
                                <div>
                                    <p className="text-gray-500 text-sm mb-2">
                                        {t('feedback-quality-rating')}:
                                    </p>
                                    <div className="flex justify-center gap-1">
                                        {renderStars(
                                            'quality',
                                            false,
                                            showQualityRating
                                        )}
                                    </div>
                                </div>
                            )}
                            {showAttitudeRating && showAttitudeRating > 0 && (
                                <div>
                                    <p className="text-gray-500 text-sm mb-2">
                                        {t('feedback-attitude-rating')}:
                                    </p>
                                    <div className="flex justify-center gap-1">
                                        {renderStars(
                                            'attitude',
                                            false,
                                            showAttitudeRating
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Show submission count if > 1 */}
                        {previousSubmission &&
                            previousSubmission.submissionCount > 1 && (
                                <p className="text-gray-400 text-xs mb-4">
                                    {t('feedback-submission-count', {
                                        count: previousSubmission.submissionCount,
                                    })}
                                </p>
                            )}
                    </div>
                </div>
            </div>
        )
    }

    const stateComponent = renderState()
    if (stateComponent) {
        return (
            <div className="min-h-screen bg-[#FEF2F2]">
                <FeedbackNavbar />
                <div className="pt-20 pb-8 px-4 flex items-center justify-center min-h-screen">
                    {stateComponent}
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#FEF2F2]">
            <FeedbackNavbar />
            <div className="pt-20 pb-8 px-4">
                <div className="max-w-2xl mx-auto">
                    {/* Main Content Card */}
                    <div className="bg-white rounded-md shadow-md p-6">
                        {/* Introduction Message */}
                        <div className="text-center mb-6">
                            <p className="text-gray-700">
                                {t('feedback-intro-message')}
                            </p>
                        </div>

                        {/* Show previous submission info - ask if want to re-rate */}
                        {previousSubmission && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                                <p className="text-amber-800 text-sm text-center">
                                    {t('feedback-previous-submission-both', {
                                        qualityRating:
                                            previousSubmission.rating_support_quality,
                                        attitudeRating:
                                            previousSubmission.rating_staff_attitude,
                                    })}
                                </p>
                                <p className="text-amber-600 text-xs text-center mt-1">
                                    {t('feedback-want-to-change')}
                                </p>
                            </div>
                        )}

                        {/* Ticket Info */}
                        <div className="mb-6 text-sm">
                            <p className="text-gray-700 font-medium mb-3">
                                {t('feedback-ticket-info')}
                            </p>
                            <div className="flex mb-2">
                                <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                    {t('feedback-ticket-code')}:
                                </span>
                                <span className="text-gray-900 font-semibold">
                                    #{ticketInfo?.ticket_id || 'N/A'}
                                </span>
                            </div>
                            {ticketInfo?.customer_name && (
                                <div className="flex mb-2">
                                    <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                        {t('feedback-customer')}:
                                    </span>
                                    <span className="text-gray-900">
                                        {ticketInfo.customer_name}
                                    </span>
                                </div>
                            )}
                            {ticketInfo?.service_name && (
                                <div className="flex mb-2">
                                    <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                        {t('feedback-service-product')}:
                                    </span>
                                    <span className="text-gray-900">
                                        {ticketInfo.service_name}
                                    </span>
                                </div>
                            )}
                            {ticketInfo?.ticket_type_title && (
                                <div className="flex mb-2">
                                    <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                        {t('feedback-ticket-type')}:
                                    </span>
                                    <span className="text-gray-900">
                                        {ticketInfo.ticket_type_title}
                                    </span>
                                </div>
                            )}
                            {/* Nhãn cũ ghi "Ngày yêu cầu" nhưng render created_at
                                — hai cột khác nhau. Tách thành hai dòng đúng cột. */}
                            <div className="flex mb-2">
                                <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                    {t('feedback-request-date')}:
                                </span>
                                <span className="text-gray-900">
                                    {formatFromUTC(
                                        ticketInfo?.ticket_requested_at
                                    )}
                                </span>
                            </div>
                            <div className="flex">
                                <span className="text-gray-600 font-medium min-w-[180px] shrink-0">
                                    {t('feedback-created-time')}:
                                </span>
                                <span className="text-gray-900">
                                    {formatFromUTC(
                                        ticketInfo?.ticket_created_at
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Feedback Form */}
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* 1. Quality Rating */}
                            <div className="mb-6">
                                <p className="text-gray-700 font-medium mb-3">
                                    1. {t('feedback-quality-rating-label')}:
                                </p>
                                <div className="flex justify-center gap-2">
                                    {renderStars('quality', true)}
                                </div>
                                {selectedRating > 0 && (
                                    <p className="text-center text-gray-500 text-sm mt-2">
                                        {t(`feedback-rating-${selectedRating}`)}
                                    </p>
                                )}
                            </div>

                            {/* 2. Attitude Rating */}
                            <div className="mb-6">
                                <p className="text-gray-700 font-medium mb-3">
                                    2. {t('feedback-attitude-rating-label')}:
                                </p>
                                <div className="flex justify-center gap-2">
                                    {renderStars('attitude', true)}
                                </div>
                                {selectedAttitudeRating > 0 && (
                                    <p className="text-center text-gray-500 text-sm mt-2">
                                        {t(
                                            `feedback-rating-${selectedAttitudeRating}`
                                        )}
                                    </p>
                                )}
                            </div>

                            {/* Feedback Text */}
                            <div className="mb-6">
                                <p className="text-gray-700 font-medium mb-3">
                                    {t('feedback-comment-label')}:
                                </p>
                                <FormItem
                                    control={control}
                                    name="feedback_text"
                                    placeholder={t(
                                        'feedback-comment-placeholder'
                                    )}
                                    type="textarea"
                                />
                            </div>

                            {/* Footer Message */}
                            <p className="text-gray-600 text-sm text-center mb-6">
                                {t('feedback-motivation-message')}
                            </p>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={
                                    selectedRating === 0 ||
                                    selectedAttitudeRating === 0 ||
                                    isSubmitting
                                }
                                className="py-3 w-full bg-[#ee2424] text-white font-medium rounded-md hover:bg-[#cc1f1f] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                {isSubmitting
                                    ? t('submitting')
                                    : previousSubmission
                                      ? t('feedback-resubmit')
                                      : t('feedback-submit')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PublicFeedback
